import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { BigNumber } from "ethers";
import { motion } from "framer-motion";
import { desaturate, lighten, rem, transparentize } from "polished";
import React, { ReactNode, useRef, useState } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { Trans, useTranslation } from "react-i18next";
import { Link, useHistory } from "react-router-dom";
import { useClickAway } from "react-use";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { useDisconnect } from "wagmi";

import {
  ampAllocationsMoleculeByPoolType,
  ampAllocationsMoleculeByPoolTypeSorted,
  ampBalanceAtom,
  ampPriceAtom,
  totalAmpSelector,
  totalWithdrawableFxcSelector,
} from "../../atoms";
import {
  Box,
  Button,
  Flex,
  H3,
  Label,
  LabelProps,
  Semi,
  Spacer,
  Text,
  TextProps,
} from "../../base";
import { getEtherscanLink } from "../../lib/ethereum";
import { formatNumber, formatTokens, formatUSD } from "../../lib/format";
import { appSelector } from "../../modules/apps";
import { autoConnectLocalStorageSelector } from "../../modules/config";
import Flexacoin from "../../modules/flexacoin";
import { useAccount, useApyByApp, useChainId } from "../../modules/hooks";
import { AmpLogo, AmpMark, AmpTiny, Bolt, CautionSign } from "../Icons";
import { FlexLoadingBlock } from "../LoadingBlock";
import LogoIcon from "../LogoIcon";
import CollapsableSection, { Section } from "./Section";

const LabelLink = Label.withComponent(Link);

const LabelExternalLink = Label.withComponent("a");

const BalanceLabel = styled(H3)`
  margin: 0;
  padding: 0;
  font-weight: 500;
`;

const Header = styled.header`
  padding: 2.25rem 2rem 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const Seperator = styled.div`
  width: 100%;
  border-radius: 2px;
  background: ${({ theme }) =>
    lighten(0.42, desaturate(0.45, theme.color?.end))};
  height: 1px;
`;

enum Asset {
  USD = "usd",
  AMP = "amp",
}

type Numberish = BigNumber | number | string;

interface Figure {
  asset: Asset | string;
  value: Numberish;
}

const AssetText = ({
  asset,
  color = "#000",
}: {
  asset: Asset | string;
  color: string;
}) => {
  switch (asset) {
    case Asset.AMP:
      return (
        <AmpMark
          color={color}
          css={css`
            position: relative;
            top: ${rem(1)};
            width: ${rem(10)};
            height: ${rem(10)};
          `}
        />
      );
    case Asset.USD:
      return <span>$</span>;
  }
  return null;
};

const FlexLabel = Label.withComponent(Flex);

const AssetLabel = ({
  figure,
  color,
  ...props
}: LabelProps &
  TextProps & {
    children?: ReactNode | ReactNode[];
    figure: Figure;
  }) => {
  const { asset, value } = figure;

  return (
    <FlexLabel center color={color} {...props}>
      {asset === Asset.USD ? (
        formatUSD(value)
      ) : (
        <>
          {formatNumber(value)}
          <Spacer col size={4} />
          <AssetText asset={asset} color={color} />
        </>
      )}
    </FlexLabel>
  );
};

const ItemButton = styled(Button)`
  position: absolute;
  right: 0;

  line-height: 1;
`;

const ItemContainer = styled(Flex)`
  position: relative;

  margin-bottom: ${rem(30)};

  &:last-of-type {
    margin-bottom: 0;
  }

  &:before {
    content: "";
    position: absolute;
    background: transparent;
    border-radius: ${rem(20)};

    top: -${rem(16)};
    bottom: -${rem(16)};
    left: -${rem(16)};
    right: -${rem(16)};

    transition: background 0.1s;
  }

  &:hover:before {
    background: ${({ theme }) => transparentize(0.97, theme.color.highlight)};
  }

  /* ${ItemButton} {
    display: none;
  }

  &:hover {
    ${ItemButton} {
      display: block;
    }
  } */
`;

const Item = ({
  title,
  figure,
  subfigure,
  pendingFigure,
  apy,
  logo,
  logoProps,
  buttonProps,
  disabled,
  light,
  ...props
}: {
  apy: string;
  buttonProps: {
    as: any;
    icon: any;
    iconProps: { fill: any };
    title: any;
    to: string;
  };
  disabled?: boolean;
  figure: {
    asset: Asset;
    value: number;
  };
  light?: boolean;
  logo: ReactNode;
  logoProps?: any;
  pendingFigure?: {
    asset: Asset;
    value: BigNumber;
  };
  subfigure: {
    asset: Asset;
    value: BigNumber;
  };
  title: string;
}) => {
  const { color } = useTheme();
  const [hover, setHover] = useState(false);

  return (
    <ItemContainer
      {...props}
      onMouseEnter={() => !disabled && setHover(true)}
      onMouseLeave={() => !disabled && setHover(false)}
    >
      <LogoIcon logo={logo} {...logoProps}></LogoIcon>
      <Spacer col size={20} />
      <div
        css={css`
          flex: 1;
        `}
      >
        <Flex between>
          <Label color={light ? "#888" : "#000"}>{title}</Label>
          {!hover && (
            <AssetLabel
              color={light ? "#888" : "#000"}
              figure={figure}
            ></AssetLabel>
          )}
        </Flex>
        <Spacer size={10} />
        <Flex between>
          {subfigure || pendingFigure ? (
            <Flex
              css={css`
                justify-content: flex-start;
                flex: 1;
              `}
            >
              {subfigure ? (
                <AssetLabel
                  small
                  color={light ? color.lighter : color.light}
                  figure={subfigure}
                />
              ) : null}
              {subfigure && pendingFigure ? (
                <span
                  css={css`
                    color: ${color.lighter};
                    margin-left: ${rem(6)};
                    margin-right: ${rem(2)};
                  `}
                >
                  +
                </span>
              ) : null}
              {pendingFigure ? (
                <>
                  {" "}
                  <AssetLabel
                    small
                    color={color.lighter}
                    figure={pendingFigure}
                  />
                </>
              ) : null}
            </Flex>
          ) : null}

          {!hover && apy ? (
            <Label small color={light ? "lighter" : "highlight"}>
              {apy}
            </Label>
          ) : null}
        </Flex>
      </div>
      {hover && buttonProps ? <ItemButton {...buttonProps} /> : null}
    </ItemContainer>
  );
};

const SuppliedRow = ({ amount, pending, appId }) => {
  const { t } = useTranslation();
  const { color } = useTheme();

  const apy = useApyByApp(appId);
  const quote = useRecoilValue(ampPriceAtom);
  const tokens = Flexacoin.toDecimal(amount);
  const pendingTokens = Flexacoin.toDecimal(pending);
  const app = useRecoilValue(appSelector(appId)) ?? { name: "[App]" };
  const { name, logo } = app;

  return (
    <div>
      <Item
        apy={`+ ${apy}% APY`}
        buttonProps={{
          title: t("common.move"),
          icon: Bolt,
          as: Link,
          to: `/account/move/${appId}`,
          iconProps: {
            fill: color.highlight,
          },
        }}
        figure={{
          asset: Asset.USD,
          value: tokens.toNumber() * quote,
        }}
        logo={logo}
        pendingFigure={
          pendingTokens.gt(0) && {
            asset: Asset.AMP,
            value: pendingTokens,
          }
        }
        subfigure={{
          asset: Asset.AMP,
          value: tokens,
        }}
        title={name}
      />
      <br></br>
      <span
        css={css`
          color: orange;
        `}
      >
        {" "}
        {app.slug === "gemini" ? t("modal.account.geminiDisabled") : ""}
      </span>
      <br></br>
      <span
        css={css`
          color: orange;
        `}
      >
        {" "}
        {app.slug === "gemini" ? t("modal.account.geminiDisabled2") : ""}
      </span>
    </div>
  );
};

const AvailableAmp = () => {
  const { t } = useTranslation();
  const { color } = useTheme();
  const quote = useRecoilValue(ampPriceAtom);
  const ampBalance = useRecoilValue(ampBalanceAtom);
  const tokens = Flexacoin.toDecimal(ampBalance);

  return (
    <CollapsableSection
      figure={
        <AssetLabel
          figure={{
            asset: Asset.AMP,
            value: tokens,
          }}
        >
          <AmpTiny
            css={css`
              width: ${rem(12)};
              height: ${rem(12)};

              margin-right: ${rem(4)};
            `}
          />{" "}
          {tokens.toString()}
        </AssetLabel>
      }
      title={t("modal.account.availableInWallet")}
    >
      <Item
        light
        apy="0.0% APY"
        buttonProps={
          tokens.gt(0) && {
            title: t("common.supply"),
            icon: Bolt,
            as: Link,
            to: "/account/supply",
            iconProps: {
              fill: color.highlight,
            },
          }
        }
        css={css`
          justify-content: space-between;
          align-items: center;
        `}
        disabled={!tokens.gt(0)}
        figure={{
          asset: Asset.USD,
          value: tokens.toNumber() * quote,
        }}
        logo={
          <AmpMark
            color="rgba(0, 0, 0, 0.2)"
            css={css`
              width: ${rem(28)};
              height: ${rem(28)};
            `}
          />
        }
        logoProps={{
          dashed: true,
          noShadow: true,
        }}
        subfigure={{
          asset: Asset.AMP,
          value: tokens,
        }}
        title="Amp"
      ></Item>
    </CollapsableSection>
  );
};

const MigrateCallout = () => {
  const { t } = useTranslation();
  const totalFxcBalance = useRecoilValue(totalWithdrawableFxcSelector);

  return (
    totalFxcBalance?.gt(0) && (
      <Section bg="rgba(255, 183, 1, 0.06)">
        <Flex between align="flex-start">
          <div>
            <Label
              light
              small
              color="rgba(0, 0, 0, 0.6)"
              css={css`
                line-height: 1.5;
              `}
            >
              <Trans
                components={{
                  Semi: <Semi />,
                }}
                i18nKey="modal.account.migrateCallout"
                values={{ tokens: formatTokens(totalFxcBalance) }}
              />
            </Label>
            <Spacer size={20} />
            <LabelLink color="warning" to="/migrate">
              {t("buttons.migrateNow")}
            </LabelLink>
          </div>
          <Spacer col size={10} />
          <CautionSign
            css={css`
              flex: 1 0 auto;
              width: ${rem(44)};
              height: ${rem(40)};
            `}
          />
        </Flex>
      </Section>
    )
  );
};

const ErrorBox = ({ error }: FallbackProps) => (
  <Box bg="rgba(255, 0, 0, 0.03)" borderRadius={10} pad={20}>
    <Text center color="rgba(88, 0, 0, 1)">
      {error.message}
    </Text>
  </Box>
);

const Container = styled(Box)`
  overflow: hidden;
  ${({ theme }) => css`
    box-shadow: 0 ${rem(4)} ${rem(12)} ${transparentize(0.9, theme.color.end)},
      0 ${rem(12)} ${rem(36)}
        ${transparentize(0.85, lighten(0.1, theme.color.end))};
  `}
`;

const AppAllocationsSection = () => {
  const allocations = useRecoilValue(
    ampAllocationsMoleculeByPoolTypeSorted("app")
  );

  return (
    <>
      {allocations?.map(({ appId, amount, pending }) => (
        <SuppliedRow
          key={appId}
          amount={amount}
          appId={appId}
          pending={pending}
        />
      )) ?? null}
    </>
  );
};

const TransformerAllocationsSection = () => {
  const allocations = useRecoilValue(
    ampAllocationsMoleculeByPoolTypeSorted("transformer")
  );

  return (
    <>
      {allocations?.map(({ appId, amount, pending }) => (
        <SuppliedRow
          key={appId}
          amount={amount}
          appId={appId}
          pending={pending}
        />
      )) ?? null}
    </>
  );
};

const useHasAllocations = (poolType: string) => {
  const allocations = useRecoilValue(
    ampAllocationsMoleculeByPoolType(poolType)
  );
  return allocations?.length > 0 ?? false;
};

const AccountModalHeader = () => {
  const { t } = useTranslation();
  const currentTotal = useRecoilValue(totalAmpSelector) ?? "0";

  return (
    <Header>
      <AmpLogo
        css={css`
          width: 4rem;
          height: 4rem;

          margin-right: 1.25rem;
        `}
        padding={0}
      />
      <div>
        <Label small color="lighter">
          {t("modal.account.totalBalance")}
        </Label>
        <Spacer size={2} />
        <BalanceLabel>{formatTokens(currentTotal)} Amp</BalanceLabel>
      </div>
    </Header>
  );
};

const modalVariants = { hidden: { opacity: 0 }, visible: { opacity: 1 } };
const sectionVariants = {
  hidden: { opacity: 0, x: 10 },
  visible: { opacity: 1, x: 0 },
};

const AccountModal = (props) => {
  const { t } = useTranslation();
  const { disconnect } = useDisconnect();
  const history = useHistory();
  const networkId = useChainId();
  const setAutoConnect = useSetRecoilState(autoConnectLocalStorageSelector);

  const ref = useRef(null);
  useClickAway(ref, () => {
    if (!history.location.pathname.includes("/account/")) {
      history.push("/");
    }
  });

  const account = useAccount();

  const onDeactivate = () => {
    setAutoConnect(false);
    disconnect();
  };

  const hasAppAllocations = useHasAllocations("app");
  const hasTransformerAllocations = useHasAllocations("transformer");

  return (
    <Container bg="#fff" borderRadius={24} pad={0} {...props}>
      <ErrorBoundary FallbackComponent={ErrorBox}>
        <motion.div
          animate="visible"
          initial="hidden"
          transition={{ staggerChildren: 0.015, delayChildren: 0.01 }}
          variants={modalVariants}
        >
          <motion.div variants={sectionVariants}>
            <React.Suspense fallback={null}>
              <AccountModalHeader />
            </React.Suspense>
          </motion.div>

          <motion.div variants={sectionVariants}>
            <React.Suspense fallback={""}>
              <MigrateCallout />
            </React.Suspense>
          </motion.div>

          <Seperator />

          <div
            css={css`
              max-height: 50vh;
              overflow: scroll;
            `}
          >
            <motion.div variants={sectionVariants}>
              <AvailableAmp />
            </motion.div>

            {hasAppAllocations ? (
              <motion.div variants={sectionVariants}>
                <CollapsableSection
                  css={css`
                    padding-top: ${rem(8)};
                  `}
                  title="Apps"
                >
                  <React.Suspense fallback={null}>
                    <AppAllocationsSection />
                  </React.Suspense>
                </CollapsableSection>
              </motion.div>
            ) : null}

            {hasTransformerAllocations ? (
              <motion.div variants={sectionVariants}>
                <CollapsableSection
                  css={css`
                    padding-top: ${rem(8)};
                  `}
                  title="Transformers"
                >
                  <React.Suspense fallback={null}>
                    <TransformerAllocationsSection />
                  </React.Suspense>
                </CollapsableSection>
              </motion.div>
            ) : null}

            <Seperator />

            <motion.div variants={sectionVariants}>
              <Section>
                <LabelExternalLink
                  color="light"
                  href={getEtherscanLink(networkId, account, "address")}
                  target="_blank"
                >
                  {t("modal.account.viewTransferHistory")}
                </LabelExternalLink>
                <Spacer size={30} />
                <LabelLink color="danger" to="/" onClick={onDeactivate}>
                  {t("modal.account.disconnectWallet")}
                </LabelLink>
              </Section>
            </motion.div>
          </div>
        </motion.div>
      </ErrorBoundary>
    </Container>
  );
};

const LoadingAccountModal = (props) => (
  <React.Suspense fallback={<FlexLoadingBlock />}>
    <AccountModal {...props} />
  </React.Suspense>
);

export default LoadingAccountModal;
